/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AVATAR_SIZE_LARGE, DataList, DataListItem, User, UserProfile } from '@squiz/sds';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { CUSTOMER_PROFILE_EMPTY_VALUE } from '../../CustomerProfile.constants';
import {
  SCV_ATTRIBUTE_CUSTOMER_ID,
  SCV_ATTRIBUTE_LAST_UPDATED,
  SCV_ATTRIBUTE_SEGMENTS,
} from '../../attributes/ScvSystemAttributes.constants';
import { GetAttributeValueType, getAttributeValue } from '../../utils/GetAttributeValue/GetAttributeValue';
import { CustomerOverviewProps } from './CustomerOverview.props';

/**
 * The content of the overview panel in the customer details tab.
 */
export const CustomerOverview = (props: CustomerOverviewProps): JSX.Element => {
  const { t } = useTranslation();
  const { customer } = props;
  const { $customerid, firstName, lastName, profileImageURL } = customer;

  // Labels
  const labels = useMemo(() => {
    return {
      overview: t(CDP_STRINGS.overview),
    };
  }, [t]);

  // Get the datalist items for the overview panel
  const getItems = (): Array<DataListItem> => {
    const overviewAttributes = [SCV_ATTRIBUTE_CUSTOMER_ID, SCV_ATTRIBUTE_SEGMENTS, SCV_ATTRIBUTE_LAST_UPDATED];

    // Should use full name (first + last) if both are present
    // Otherwise, just fall back to customerId instead
    const userProfile: UserProfile = {
      email: ``,
      firstName: firstName && lastName ? firstName : $customerid,
      lastName: firstName && lastName ? lastName : ``,
      photo: profileImageURL,
    };

    // Map each attribute to a datalist item
    return overviewAttributes.map((attribute, index): DataListItem => {
      const { key, title, type } = attribute;

      let value = CUSTOMER_PROFILE_EMPTY_VALUE;
      if (customer[key]) {
        value = getAttributeValue(customer[key] as GetAttributeValueType, type, t);
      }

      const content = (
        <div className="customer-profile-item">
          {index === 0 && (
            <User
              avatarSize={AVATAR_SIZE_LARGE}
              classes="customer-overview__user"
              hideSurname={!lastName}
              user={userProfile}
            />
          )}
          <span className="customer-profile-item__name">{title}</span>
          <span className="customer-profile-item__value">{value}</span>
        </div>
      );
      return { content, isCustom: true, key: `customer-overview-item--${index}` } as DataListItem;
    });
  };

  return (
    <DataList
      classes="customer-overview__wrapper"
      empty={<></>}
      isInteractive={false}
      items={getItems()}
      title={labels.overview}
    />
  );
};
